import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, Panel } from "@/components/AppShell";
import { GoalGauge } from "@/components/GoalGauge";
import { BalanceChart } from "@/components/BalanceChart";
import { AllocationDonut } from "@/components/AllocationDonut";
import { FundWatchlist } from "@/components/FundWatchlist";
import { NewsFeed } from "@/components/NewsFeed";
import { QuickWhatIf } from "@/components/QuickWhatIf";
import { MissedMatchAlert } from "@/components/MissedMatchAlert";
import { RetirementCountdown } from "@/components/RetirementCountdown";
import { ShareSnapshot } from "@/components/ShareSnapshot";
import { QuizPromo } from "@/components/QuizPromo";
import { TspUpdateNudge } from "@/components/TspUpdateNudge";
import { usePortfolio } from "@/context/ProfileContext";
import { api, errMsg } from "@/lib/api";
import { project } from "@/lib/finance";
import { effectiveGoal } from "@/lib/fers";
import { usd } from "@/lib/format";
import { useTour } from "@/components/Onboarding";
import { Compass } from "@phosphor-icons/react";

function TourButton() {
  const { start } = useTour();
  return <Button variant="outline" className="rounded-full" onClick={start} data-testid="dashboard-take-tour-button"><Compass size={16} className="mr-1.5" />Take the tour</Button>;
}

function Kpi({ label, value, sub, hero, testid }) {
  return (
    <div className="glass glass-lift p-5 fade-up" data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-3 font-mono-num text-2xl sm:text-3xl font-semibold ${hero ? "text-grad-money" : "text-slate-900 dark:text-white"}`} data-testid={`${testid}-value`}>{value}</p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

export function SyncButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const sync = async () => {
    setBusy(true);
    try {
      const r = await api.post("/sync");
      toast.success(`Synced. Net worth ${usd(r.data.totals.total)}`);
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button variant="outline" className="rounded-full gap-2" onClick={sync} disabled={busy} data-testid="sync-now-button">
      <ArrowsClockwise size={16} className={busy ? "animate-spin" : ""} /> {busy ? "Syncing..." : "Sync Now"}
    </Button>
  );
}

export default function Dashboard() {
  const { profile, dash, loadDash } = usePortfolio();
  const t = dash?.totals || {};
  const proj = useMemo(() => (profile && dash ? project(profile, t.total || 0) : null), [profile, dash, t.total]);
  const target = profile ? effectiveGoal(profile).target : 0;
  const ratio = proj && target ? proj.final.expected / target : 0;
  const ira = (t.trad_ira || 0) + (t.roth_ira || 0);
  const empty = dash && !dash.holdings?.length;
  return (
    <div>
      <PageHeader title="Dashboard" subtitle={dash?.meta?.last_sync ? `Last synced ${new Date(dash.meta.last_sync).toLocaleString()}` : "Your TSP and IRA picture at a glance."}
        actions={<><TourButton /><ShareSnapshot profile={profile} totals={t} /><SyncButton onDone={loadDash} /><QuickWhatIf profile={profile} balance={t.total || 0} /></>} />
      <MissedMatchAlert profile={profile} />
      <RetirementCountdown profile={profile} />
      <TspUpdateNudge />
      <QuizPromo testid="dashboard-quiz-card" className="mb-6" />
      {empty && (
        <div className="glass p-5 mb-6 flex flex-wrap items-center justify-between gap-3 fade-up" data-testid="dashboard-empty-banner">
          <p className="text-sm">Start by adding your TSP fund shares and IRA holdings, or import a TSP statement.</p>
          <Link to="/accounts"><Button className="rounded-full" data-testid="go-to-accounts-button">Set up accounts</Button></Link>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <Kpi label="Total Net Worth" value={usd(t.total)} sub="TSP + IRAs + other" hero testid="kpi-net-worth" />
        <Kpi label="Current TSP Balance" value={usd(t.tsp)} sub={dash?.meta?.loan_balance ? `Loan outstanding ${usd(dash.meta.loan_balance)}` : "Valued at latest official close"} testid="kpi-tsp-balance" />
        <Kpi label="Aggregated IRA Balance" value={usd(ira)} sub={`Traditional ${usd(t.trad_ira)} · Roth ${usd(t.roth_ira)}`} testid="kpi-ira-balance" />
        <div className="glass glass-lift p-5 fade-up flex items-center justify-between gap-2" data-testid="kpi-goal">
          <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Goal Progress</p><p className="mt-2 text-xs text-muted-foreground">Projected vs {usd(target)}</p><Link to="/goals" className="text-xs text-cyan-700 dark:text-cyan-400 underline">Details</Link></div>
          <GoalGauge ratio={ratio} size={130} />
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 mt-6">
        <Panel title="Balance history vs. projected growth" className="lg:col-span-2" testid="panel-balance-chart">
          <BalanceChart history={dash?.history || []} projection={proj?.series || []} target={target} />
        </Panel>
        <Panel title="Allocation" testid="panel-allocation"><AllocationDonut data={dash?.allocation || []} /></Panel>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 mt-6">
        <Panel title="Live Fund Watchlist" action={<Link to="/funds" className="text-xs text-cyan-700 dark:text-cyan-400">All prices</Link>} testid="panel-watchlist"><FundWatchlist /></Panel>
        <Panel title="Projection snapshot" testid="panel-projection">
          {proj && (
            <div className="space-y-3 text-sm">
              {[["Conservative", proj.final.conservative], ["Expected", proj.final.expected], ["Optimistic", proj.final.optimistic]].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-border/60 pb-2"><span className="text-muted-foreground">{k} at {profile.retire_age}</span><span className="font-mono-num font-medium">{usd(v)}</span></div>
              ))}
              <p className="text-xs text-muted-foreground">Blended return {proj.rate.toFixed(2)}% from your contribution allocation; cone is ±2%.</p>
            </div>
          )}
        </Panel>
        <Panel title="Latest Retirement News" testid="panel-news"><NewsFeed /></Panel>
      </div>
    </div>
  );
}
